import api from '@/shared/api';
import { NOTIFICATION_EXPORT_GENERIC_RETRY_MESSAGE } from '../config/messages';
import {
  buildTruthySearchParams,
  requestExcelBlobExport,
  type NotificationApiResponse,
} from '@/shared/lib';
import type { NotificationSearchType } from '../model/types';

export interface NotificationListFiltersParams {
  term?: string;
  sort?: string;
  order?: string;
  searchType?: NotificationSearchType;
  createdBy?: number;
  status?: string;
  periodicity?: string;
  startDate?: string;
  endDate?: string;
  customPeriodicity?: number;
  customPeriodicityUnit?: string;
}

const NOTIFICATION_LIST_FILTER_QUERY_KEYS = [
  'term',
  'sort',
  'order',
  'searchType',
  'createdBy',
  'status',
  'periodicity',
  'startDate',
  'endDate',
  'customPeriodicity',
  'customPeriodicityUnit',
] as const satisfies readonly (keyof NotificationListFiltersParams)[];

export interface NotificationEditData {
  id: number;
  title: string;
  periodicity: string;
  customDays?: number;
  startDate?: string;
  endDate?: string;
  customDates?: string[];
  roles: { id: number; name: string }[];
  collaborators: {
    id: number;
    name: string;
    role: string;
    positions: { id: number; name: string }[];
    status?: string | null;
    deletedAt?: string | null;
    active: boolean;
  }[];
  description: string;
}

export interface CreateNotificationPayload {
  title: string;
  description: string;
  periodicity: 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY' | 'CUSTOM';
  customDays?: number;
  startDate?: string;
  endDate?: string;
  customDates?: string[];
  selectedUsers: number[];
  isDraft?: boolean;
}

export interface NotificationRecipientMember {
  id: number;
  userId: number;
  name: string;
  email: string;
  positions: Array<{ id: number; name: string }>;
}

class NotificationApi {
  async getNotifications(
    params?: NotificationListFiltersParams & {
      page?: number;
      itemsPerPage?: number;
    }
  ): Promise<NotificationApiResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.itemsPerPage) queryParams.append('itemsPerPage', params.itemsPerPage.toString());
    const filterQueryParams = buildTruthySearchParams(
      params ?? {},
      NOTIFICATION_LIST_FILTER_QUERY_KEYS
    );
    filterQueryParams.forEach((value, key) => {
      queryParams.append(key, value);
    });

    const response = await api.get(`/notifications?${queryParams.toString()}`);
    return response.data;
  }

  async exportNotificationsExcel(
    params: NotificationListFiltersParams
  ): Promise<{ blob: Blob; exportedCount: number; filename: string }> {
    const queryParams = buildTruthySearchParams(params, NOTIFICATION_LIST_FILTER_QUERY_KEYS);

    return requestExcelBlobExport(
      () =>
        api.get(`/notifications/export/excel?${queryParams.toString()}`, {
          responseType: 'blob',
        }),
      'notificacoes.xlsx',
      NOTIFICATION_EXPORT_GENERIC_RETRY_MESSAGE
    );
  }

  async deleteNotification(notificationId: number): Promise<void> {
    await api.delete(`/notifications/${notificationId}`);
  }

  async bulkDeleteNotifications(notificationIds: number[]): Promise<{
    totalRequested: number;
    successCount: number;
    failureCount: number;
    succeededIds: number[];
    failedOperations: Array<{ id: number; error: string }>;
    invalidIds: number[];
  }> {
    const response = await api.delete('/notifications/bulk', {
      data: { ids: notificationIds },
    });
    return response.data.data;
  }

  async createNotification(data: CreateNotificationPayload): Promise<void> {
    await api.post('/notifications', data);
  }

  async getNotificationForEdit(id: number): Promise<NotificationEditData> {
    const response = await api.get(`/notifications/${id}/edit`);
    return response.data.data;
  }

  async updateNotification(id: number, data: CreateNotificationPayload): Promise<void> {
    await api.put(`/notifications/${id}`, data);
  }

  async getRecipients(): Promise<NotificationRecipientMember[]> {
    const response = await api.get('/notifications/recipients');
    return response.data.data;
  }
}

export const notificationApi = new NotificationApi();
